import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { debounceTime, distinctUntilChanged, forkJoin, Subject, takeUntil } from 'rxjs';

import { LayoutComponent } from '../../../shared/layouts/layout/layout.component';
import { TaskDetailsComponent } from '../../components/task-details/task-details.component';
import { TasksService } from '../../services/tasks/tasks.service';

@Component({
  selector: 'app-tasks',
  standalone: true,
  imports: [
    AccordionModule,
    AutoCompleteModule,
    ButtonModule,
    CalendarModule,
    CommonModule,
    DialogModule,
    DropdownModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    InputTextareaModule,
    LayoutComponent,
    PaginatorModule,
    ReactiveFormsModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './tasks.component.html',
  styleUrl: './tasks.component.scss'
})
export class TasksComponent implements OnInit, OnDestroy {
  private _tasksService = inject(TasksService);
  private _dialogService = inject(DialogService);
  public taskForm: FormGroup;

  public tasks: any[] = [];
  public loading: boolean = false;
  public isModalVisible: boolean = false;

  public tasksTypes: any[] = [];
  public nestingAreas: any[] = [];

  public dataForm!: FormGroup;
  private filterSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  public activeView: 'table' | 'kanban' = 'table';
  public kanbanColumns: any[] = [];
  public page_size: number = 10;
  public draggedTask: any = null;
  public dragOverColumnId: string | null = null;

  public statusOptions: any[] = [];
  public filteredStatus: any[] = [];

  private taskDetailsDialogRef: DynamicDialogRef | undefined;

  constructor(
    private _messageService: MessageService,
    private _formBuilder: FormBuilder
  ) {
    this.taskForm = this._formBuilder.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required]],
      initial_date: ['', [Validators.required]],
      final_date: ['', [Validators.required]],
      areas: [[], [Validators.required]],
      task_type: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.activeView = (localStorage.getItem('activeView') as 'table' | 'kanban') || 'table';
    this.page_size = Number(localStorage.getItem('page_size')) || 10;

    this.loadDataForm();
    this.loadStatusOptions();
    this.loadData();

    if (this.activeView === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadTasks();
    }

    this.filterSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.activeView === 'kanban') {
        this.loadDataKanban();
      } else {
        this.loadTasks();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.taskDetailsDialogRef) {
      this.taskDetailsDialogRef.close();
    }
  }

  onFilterInput(event: any, filter: string) {
    const inputElement = event.target as HTMLInputElement;
    this.dataForm.value[filter] = inputElement.value;

    this.filterSubject.next(inputElement.value);
  }

  resetFilters() {
    this.loadDataForm();

    if (this.activeView === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadTasks();
    }
  }

  loadTasks() {
    this.loading = true;

    const filters = {
      ...this.dataForm.value,
      status: this.dataForm.value.status instanceof Object ? this.dataForm.value.status.id : this.dataForm.value.status,
      task_type: this.dataForm.value.task_type instanceof Object ? this.dataForm.value.task_type.id : this.dataForm.value.task_type,
    };

    this._tasksService.getTasks(filters).subscribe((data: any) => {
      this.tasks = data.items;

      this.dataForm.value.totalRecords = data.total;
      this.dataForm.value.page = data.page;
      this.dataForm.value.page_size = data.page_size;

      this.loading = false;
    });
  }

  loadDataKanban(): void {
    this.loading = true;

    this._tasksService.getStatusTasks().subscribe({
      next: (statusRes: any) => {
        const statusArray = statusRes.items || statusRes;
        this.kanbanColumns = statusArray.map((status: any) => ({
          statusId: status.id,
          title: status.name,
          color: status.color || '#4459a4',
          tasks: [],
          loading: false,
          currentPage: 1,
          totalPages: 1,
          total: 0
        }));

        this.kanbanColumns.forEach(column => {
          this.loadKanbanColumnData(column);
        });

        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error('Error loading statuses:', error);
      }
    });
  }

  loadKanbanColumnData(column: any, page: number = 1): void {
    column.loading = true;

    const params: any = {
      ...this.dataForm.value,
      page: page,
      page_size: this.page_size,
      status: column.statusId,
      search: this.dataForm.value.search || '',
    };

    this._tasksService.getTasks(params).subscribe({
      next: (response: any) => {
        if (page === 1) {
          column.tasks = response.items;
        } else {
          column.tasks = [...column.tasks, ...response.items];
        }

        column.currentPage = response.page;
        column.totalPages = Math.ceil(response.total / this.page_size);
        column.total = response.total;
        column.loading = false;
      },
      error: (error) => {
        column.loading = false;
        console.error('Error loading column data:', error);
      }
    });
  }

  loadMoreKanbanItems(column: any): void {
    if (column.currentPage < column.totalPages) {
      this.loadKanbanColumnData(column, column.currentPage + 1);
    }
  }

  toggleView(view: 'table' | 'kanban') {
    this.activeView = view;
    localStorage.setItem('activeView', this.activeView);

    if (view === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadTasks();
    }
  }

  onDragStart(event: DragEvent, task: any): void {
    this.draggedTask = task;
    event.dataTransfer!.effectAllowed = 'move';
  }

  onDragEnd(event: DragEvent): void {
    this.draggedTask = null;
  }

  onDrop(event: DragEvent, targetStatusId: string): void {
    event.preventDefault();
    this.dragOverColumnId = null;

    if (this.draggedTask && this.draggedTask.status.id !== targetStatusId) {
      this.updateTaskStatus(this.draggedTask, targetStatusId);
    }
  }

  onDragOver(event: DragEvent, columnId: string): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.dragOverColumnId = columnId;
  }

  onDragLeave(event: DragEvent): void {
    this.dragOverColumnId = null;
  }

  updateTaskStatus(task: any, newStatusId: string): void {
    this.loading = true;

    const data = {
      status: newStatusId
    };

    this._tasksService.patchTask(data, task.id).subscribe({
      next: (res) => {
        this._messageService.add({
          severity: 'success',
          summary: '¡Éxito!',
          detail: 'El estado de la tarea se ha actualizado correctamente'
        });

        if (this.activeView === 'kanban') {
          this.loadDataKanban();
        } else {
          this.loadTasks();
        }

        this.loading = false;
      },
      error: (err) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar el estado de la tarea'
        });
        this.loading = false;
      }
    });
  }

  loadStatusOptions(): void {
    this._tasksService.getStatusTasks().subscribe({
      next: (res: any) => {
        this.statusOptions = res.items || res;
      },
      error: (err) => {
        console.error('Error loading status options:', err);
      },
    });
  }

  filterStatus(event: any) {
    let filtered: any[] = [];
    let query = event.query;

    for (let i = 0; i < this.statusOptions.length; i++) {
      let status = this.statusOptions[i];
      if (status.name.toLowerCase().indexOf(query.toLowerCase()) == 0) {
        filtered.push(status);
      }
    }

    this.filteredStatus = filtered;
  }

  selectField(event: any, field: string) {
    const value = event ? (event.value || event) : '';

    this.dataForm.patchValue({
      [field]: value
    });

    if (this.activeView === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadTasks();
    }
  }

  getStatusClass(status: any): string {
    const statusMap: any = {
      'task-todo': 'status-red',
      'task-inprogress': 'status-yellow',
      'task-done': 'status-green',
      'task-canceled': 'status-gray',
    };
    return statusMap[status.key_string] || 'status-blue';
  }

  loadDataForm(): void {
    this.dataForm = this._formBuilder.group({
      totalRecords: 0,
      page: 1,
      page_size: this.page_size,
      firstPage: 0,
      search: '',
      status: '',
      task_type: '',
    });
  }

  loadData(): void {
    forkJoin({
      tasksTypes: this._tasksService.getTaskTypes(),
      nestingAreas: this._tasksService.getNestingAreas(),
    }).subscribe((data: any) => {
      this.tasksTypes = data.tasksTypes.items;
      this.nestingAreas = data.nestingAreas.items;
    });
  }

  handleSave(event: Event): void {
    event.preventDefault();

    const initialDate = new Date(this.taskForm.value.initial_date);
    const finalDate = new Date(this.taskForm.value.final_date);
    const formattedInitialDate = initialDate.toISOString().split('T')[0];
    const formattedFinalDate = finalDate.toISOString().split('T')[0];

    if (this.taskForm.valid) {
      this._tasksService.createTask(
        this.taskForm.value.title,
        this.taskForm.value.description,
        formattedInitialDate,
        formattedFinalDate,
        this.taskForm.value.areas.map((area: any) => area.id),
        this.taskForm.value.task_type.id
      ).subscribe({
        next: (data: any) => {
          this._messageService.add({
            severity: 'success',
            summary: '¡Éxito!',
            detail: '¡La tarea se ha creado correctamente!'
          });

          this.loadDataForm();
          this.loadTasks();

          this.isModalVisible = false;
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Hubo un problema al crear la tarea. Por favor, inténtelo de nuevo.'
          });

          console.error('Error al crear la tarea:', error);
        }
      });

      this.isModalVisible = false;
    } else {
      this.taskForm.markAllAsTouched();
    }
  }

  filterTask(event: any) {
    const query = event
      ? event.query.toLowerCase()
      : '';

    this._tasksService.getTaskTypes(query).subscribe((data: any) => {
      this.tasksTypes = data.items;
    });
  }

  filterArea(event: any) {
    const query = event
      ? event.query.toLowerCase()
      : '';

    this._tasksService.getNestingAreas(query).subscribe((data: any) => {
      this.nestingAreas = data.items;
    });
  }

  pageChange(event: any) {
    const newPage = event.first / event.rows;

    this.dataForm.value.firstPage = event.first;
    this.dataForm.value.page = newPage + 1;

    this.loadTasks();
  }

  getSeverity(task: any): any {
    const SEVERITY_DICT: any = {
      'task-todo': 'danger',
      'task-inprogress': 'warning',
      'task-done': 'success',
    }

    return SEVERITY_DICT[task.status.key_string] || 'info';
  }

  viewTaskDetails(task: any): void {
    this.taskDetailsDialogRef = this._dialogService.open(TaskDetailsComponent, {
      header: 'Detalles de la Tarea',
      width: '90%',
      height: '90vh',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: true,
      data: {
        task: task
      }
    });

    this.taskDetailsDialogRef.onClose.subscribe(() => {
      if (this.activeView === 'kanban') {
        this.loadDataKanban();
      } else {
        this.loadTasks();
      }
    });
  }
}
